import type { CategoryPricePoint } from '@/api/market'
import { useNotLivePage } from '@/app/notLive'
import { SectionFrame } from '@/app/pages'
import type { RouteDef } from '@/app/routes'
import { Card, DataTable, EmptyState, formatCurrency, formatInteger, formatPercent, LoadingBlock, type Column } from '@/design'

import { useMarketQuery, useMoney } from './hooks'
import { NotLiveSection } from './NotLiveSection'
import './market.css'

function columns(money: ReturnType<typeof useMoney>): Column<CategoryPricePoint>[] {
  return [
    { key: 'category', header: 'Category', sortValue: (row) => row.category, cell: (row) => row.category },
    { key: 'count', header: 'Tracked', align: 'end', sortValue: (row) => row.trackedCount, cell: (row) => formatInteger(row.trackedCount) },
    {
      key: 'price',
      header: 'Average ticket price',
      align: 'end',
      sortValue: (row) => row.averageTicketPrice,
      cell: (row) => formatCurrency(row.averageTicketPrice, money),
    },
    {
      key: 'entries',
      header: 'Average max entries',
      align: 'end',
      sortValue: (row) => row.averageMaxEntries,
      cell: (row) => formatInteger(row.averageMaxEntries),
    },
    {
      key: 'instant',
      header: 'With instant wins',
      align: 'end',
      sortValue: (row) => row.instantWinShare,
      cell: (row) => formatPercent(row.instantWinShare, 0),
    },
  ]
}

/** Average ticket price, common categories, volume, entry caps and promotional patterns across the current market. */
export function PricingPage({ route }: { route: RouteDef }) {
  const notLive = useNotLivePage('marketPricingSummary')
  const pricing = useMarketQuery('pricing-summary', (client, filters) => client.marketPricingSummary(filters))
  const money = useMoney()

  if (notLive) return <NotLiveSection route={route} />

  return (
    <SectionFrame route={route}>
      <div className="ci-stack">
        {pricing.isPending ? <LoadingBlock rows={4} /> : null}
        {pricing.isError ? (
          <Card>
            <EmptyState variant="outage" compact description="Pricing could not be loaded." />
          </Card>
        ) : null}
        {pricing.data ? (
          <>
            <ul className="mk-stats" aria-label="Pricing summary">
              <li>
                <span className="mk-stats__value">{formatCurrency(pricing.data.data.averageTicketPrice, money)}</span>
                <span className="mk-stats__label">Average ticket price</span>
              </li>
              <li>
                <span className="mk-stats__value">{formatInteger(pricing.data.data.volume)}</span>
                <span className="mk-stats__label">Tracked competitions live now</span>
              </li>
              <li>
                <span className="mk-stats__value">{formatInteger(pricing.data.data.averageMaxEntries)}</span>
                <span className="mk-stats__label">Average ticket cap</span>
              </li>
              <li>
                <span className="mk-stats__value">{formatPercent(pricing.data.data.instantWinShare, 0)}</span>
                <span className="mk-stats__label">Include instant wins</span>
              </li>
            </ul>

            <Card title="Common categories" subtitle="Most-tracked prize categories right now">
              <p className="mk-tags">{pricing.data.data.commonCategories.join(', ') || 'None tracked yet'}</p>
            </Card>

            <Card title="Promotional patterns" subtitle="Messaging that recurs across more than one competitor">
              {pricing.data.data.promotionalPatterns.length > 0 ? (
                <ul className="mk-list">
                  {pricing.data.data.promotionalPatterns.map((pattern) => (
                    <li key={pattern}>{pattern}</li>
                  ))}
                </ul>
              ) : (
                <EmptyState variant="no-data" compact title="No recurring patterns yet" />
              )}
            </Card>

            <Card title="Pricing by category">
              <DataTable
                caption="Pricing by category"
                columns={columns(money)}
                rows={pricing.data.data.byCategory}
                rowKey={(row) => row.category}
                initialSort={{ key: 'count', direction: 'desc' }}
                empty={<EmptyState variant="no-data" compact title="No tracked competitions yet" />}
              />
            </Card>
          </>
        ) : null}
      </div>
    </SectionFrame>
  )
}
